import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import {
  Button,
  Popconfirm,
  Steps,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'
import { blue, redPrimary } from '../../color'
import { ASSIGNEE_TYPE, PROPOSAL_STATUS } from '../../constants'
import date_format from '../../date_format'
import { AvatarHasName } from '../Common/Avatar'
import SelectPeoplePopup from '../SelectPeoplePopup'

const { Step } = Steps

const StepsWrapper = styled.div`
  margin-top: 20px;
  margin-bottom: 30px;
  position: relative;

  .steps {
    width: 100% !important;
    display: ${({ count, i }) => (count > i ? 'flex' : '')};

    /* Cho phép xuống dòng khi có nhiều items */
    flex-wrap: ${({ count }) => (count > 4 ? 'wrap' : 'nowrap')};

    /* Căn chỉnh các items */
    justify-content: ${({ count }) => (count > 4 ? 'flex-start' : 'center')};
    align-items: flex-start;

    transform: translateX(30px);

    .ant-steps-item {
      /* Đặt width cho mỗi item để tối đa 4 items trên 1 hàng */
      flex: ${({ count }) => (count > 4 ? '0 0 25%' : '1')};
      max-width: ${({ count }) => (count > 4 ? '25%' : 'none')};

      /* Margin bottom cho hàng thứ 2 */
      margin-bottom: ${({ count }) => (count > 4 ? '20px' : '0')};
    }

    .ant-steps-item-title,
    .ant-steps-item-description {
      color: #000000d9 !important;
    }

    .ant-steps-item-wait .ant-steps-item-icon > .ant-steps-icon {
      color: #000000d9 !important;
      font-weight: 500;
    }

    .ant-steps-item-content {
      width: ${({ i, count }) => {
        if (count > 4) return '200px'
        return i == 4 ? '225px' : '220px'
      }} !important;
      transform: translateX(-30px);
    }

    .tag-selected {
      border-radius: 35px;
      padding: 2px 8px 2px 3px;
      display: flex;
      margin-right: 0;
      align-items: center;
    }
  }

  /* Điều chỉnh vị trí icon và tail cho layout 2 hàng */
  .ant-steps-label-vertical.ant-steps-small:not(.ant-steps-dot)
    .ant-steps-item-icon {
    margin-left: ${({ count }) => (count > 4 ? '50px' : '70px')} !important;
  }

  .ant-steps-label-vertical .ant-steps-item-tail {
    margin-left: ${({ count }) => (count > 4 ? '66px' : '86px')};
    width: ${({ count }) => (count > 4 ? '80%' : '90%')};

    /* Ẩn tail cho item cuối của mỗi hàng */
    &:nth-child(4n) {
      display: ${({ count }) => (count > 4 ? 'none' : 'block')};
    }
  }

  /* Responsive cho màn hình lớn */
  @media screen and (min-width: 1600px) {
    .steps {
      display: ${({ i, count }) => {
        if (count > 4) return 'flex'
        return i == 5 ? 'flex' : '-webkit-box'
      }} !important;

      .ant-steps-item {
        flex: ${({ count }) => (count > 4 ? '0 0 20%' : '1')};
        max-width: ${({ count }) => (count > 4 ? '20%' : 'none')};
      }
    }

    /* Có thể hiển thị 5 items trên 1 hàng cho màn hình lớn */
    .ant-steps-label-vertical .ant-steps-item-tail {
      &:nth-child(5n) {
        display: ${({ count }) => (count > 4 ? 'none' : 'block')};
      }
    }
  }

  /* Responsive cho màn hình nhỏ */
  @media screen and (max-width: 768px) {
    .steps {
      .ant-steps-item {
        flex: ${({ count }) => (count > 4 ? '0 0 50%' : '1')};
        max-width: ${({ count }) => (count > 4 ? '50%' : 'none')};
      }
    }

    .ant-steps-label-vertical .ant-steps-item-tail {
      &:nth-child(2n) {
        display: ${({ count }) => (count > 4 ? 'none' : 'block')};
      }
    }
  }
`

const getInfoUser = (username, departmentWithUserList) => {
  const defaultUser = {
    full_name: username || 'Unknown User',
    image_id: null,
    username: username || null,
    department: null,
  }

  if (!username) {
    return defaultUser
  }

  for (const department of departmentWithUserList) {
    const userTarget = department.users.find(
      user => user.user_name === username
    )
    if (userTarget) {
      return {
        full_name: userTarget.name_uppercase || username,
        image_id: userTarget.image_id || null,
        username: username,
        department: department.name || null,
      }
    }
  }

  return defaultUser
}

const renderStatus = (status, isRejected) => {
  const styleTag = {
    marginTop: 8,
    borderRadius: 10,
  }

  if (status === PROPOSAL_STATUS.PENDING) {
    return (
      <Tag
        color={isRejected ? null : 'geekblue'}
        style={{
          ...styleTag,
          opacity: isRejected ? 0.5 : 1,
        }}>
        Đến lượt duyệt
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.WAITING) {
    return (
      <Tag
        color={isRejected ? null : 'orange'}
        style={{
          ...styleTag,
          opacity: isRejected ? 0.5 : 1,
        }}>
        Chờ duyệt
      </Tag>
    )
  }

  if (status === PROPOSAL_STATUS.CREATED) {
    return (
      <Tag color={'cyan'} style={styleTag}>
        Đã tạo
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.APPROVED) {
    return (
      <Tag color={'green'} style={styleTag}>
        Đã duyệt
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.REJECTED) {
    return (
      <Tag color={'red'} style={styleTag}>
        Đã từ chối
      </Tag>
    )
  }
}

const renderTime = level => {
  const styleTag = {
    marginTop: 4,
  }

  const textCreated = date_format.HH_mm_DD_MM_YYYY(level.created_at)
  const textUpdated = date_format.HH_mm_DD_MM_YYYY(level.updated_at)

  if (level.status === PROPOSAL_STATUS.CREATED) {
    return (
      <p color={'cyan'} style={styleTag}>
        {textCreated}
      </p>
    )
  }
  if (
    level.status === PROPOSAL_STATUS.APPROVED ||
    level.status === PROPOSAL_STATUS.REJECTED
  ) {
    return (
      <p color={'cyan'} style={styleTag}>
        {textUpdated}
      </p>
    )
  }

  return <></>
}

export const ASSIGNEE_TYPE_CONFIG = [
  ASSIGNEE_TYPE.CREATE,
  ASSIGNEE_TYPE.COMBINER,
  ASSIGNEE_TYPE.HANDLER,
  ASSIGNEE_TYPE.LEADER,
  ASSIGNEE_TYPE.UNIT_LEADER,
  ASSIGNEE_TYPE.HDQT,
  ASSIGNEE_TYPE.KT_KTNB,
  ASSIGNEE_TYPE.lanh_dao_vimc,
]

const ProposalStepApproveDefault = props => {
  const {
    proposalStore,
    selectPeopleStore,
    approvalLevelList,
    styleCre,
  } = props

  const { originDepartmentWithUserList, selectUserData } = selectPeopleStore
  const [currentStep, setCurrentStep] = useState(0)
  const [approvalLevels, setApprovalLevels] = useState([])
  const [isVisibleSelectUserPopup, setIsVisibleSelectUserPopup] = useState(
    false
  )
  const [statusStep, setStatusStep] = useState('process')

  useEffect(() => {
    const length = approvalLevelList.length
    if (approvalLevelList.length === 0) return

    let currentIndex = 0
    const stepRejected = approvalLevelList.find(
      item => item.status === PROPOSAL_STATUS.REJECTED
    )

    // Tạo mảng các bước dựa trên thứ tự trong approvalLevelList
    const steps = approvalLevelList.map((item, index) => ({
      index: index,
      level: item.level,
      status: item.status,
    }))

    // Tìm chỉ số của bước hiện tại
    const pendingStep = steps.find(
      item => item.status === PROPOSAL_STATUS.PENDING
    )
    const approvedSteps = steps.filter(
      item => item.status === PROPOSAL_STATUS.APPROVED
    )
    const maxApprovedIndex =
      approvedSteps.length > 0
        ? Math.max(...approvedSteps.map(item => item.index))
        : -1

    if (pendingStep) {
      currentIndex = pendingStep.index
    } else if (maxApprovedIndex >= 0) {
      // Chuyển đến bước tiếp theo sau bước đã duyệt
      currentIndex = maxApprovedIndex + 1
    } else if (stepRejected) {
      currentIndex = steps.findIndex(item => item.level === stepRejected.level)
      setStatusStep('error')
    } else if (
      approvalLevelList.every(item => item.status === PROPOSAL_STATUS.APPROVED)
    ) {
      currentIndex = length
    }

    setCurrentStep(currentIndex)
    setStatusStep(stepRejected ? 'error' : '')
  }, [approvalLevelList])

  useEffect(() => {
    const approvalLevelsUpdate = approvalLevels.map(item => {
      if (!item.allow_edit) {
        return item
      }
      const assignTypeIndex = approvalLevels
        .filter(el => el.allow_edit)
        .findIndex(el => el.level === item.level)
      const getUser = index => {
        if (index < 0 || index >= ASSIGNEE_TYPE_CONFIG.length) {
          return {}
        }
        return selectUserData[ASSIGNEE_TYPE_CONFIG[index]]?.length > 0
          ? selectUserData[ASSIGNEE_TYPE_CONFIG[index]][0]
          : {}
      }
      const assigneeType =
        assignTypeIndex < ASSIGNEE_TYPE_CONFIG.length
          ? ASSIGNEE_TYPE_CONFIG[assignTypeIndex]
          : `DYNAMIC_LEVEL_${assignTypeIndex}`
      const selectedUser =
        selectUserData[assigneeType]?.length > 0
          ? selectUserData[assigneeType][0]
          : item.user || {}
      const userInfo = getInfoUser(
        selectedUser.id || item.username,
        originDepartmentWithUserList
      )
      return {
        ...item,
        username: getUser(assignTypeIndex).id,
        user: {
          full_name: getUser(assignTypeIndex).full_name,
          image_id: getUser(assignTypeIndex).image_id,
          username: getUser(assignTypeIndex).id,
          department: userInfo.department,
        },
      }
    })
    selectPeopleStore.setSelectUserData(selectUserData)
    setApprovalLevels(approvalLevelsUpdate)
  }, [selectUserData])

  useEffect(() => {
    proposalStore.setDefaultApprovalConfigUpdate(approvalLevels)
  }, [approvalLevels])

  useEffect(() => {
    const approvalLevelConvert = approvalLevelList.map(item => {
      return {
        ...item,
        user: getInfoUser(item.username, originDepartmentWithUserList),
      }
    })

    const levelAllowEditList = approvalLevelConvert.filter(
      item => item.allow_edit
    )

    const selectUserData = {}

    levelAllowEditList.forEach((item, index) => {
      if (!item.username || index >= ASSIGNEE_TYPE_CONFIG.length) return
      const user = getInfoUser(item.username, originDepartmentWithUserList)
      const assigneeType = ASSIGNEE_TYPE_CONFIG[index]
      selectUserData[assigneeType] = [
        {
          assignee_type: ASSIGNEE_TYPE.USER,
          full_name: user.full_name,
          id: user.username,
          image_id: user.image_id,
          permission: assigneeType,
        },
      ]
    })

    selectPeopleStore.setSelectUserData(selectUserData)

    setApprovalLevels(approvalLevelConvert)
  }, [approvalLevelList, originDepartmentWithUserList])

  const handleSubmitSelectUser = useCallback(() => {
    setIsVisibleSelectUserPopup(false)
  }, [])

  const handleOpenSelectUser = level => {
    let userType = null
    const levelAllowEditList = approvalLevelList.filter(item => item.allow_edit)
    const indexLevelAllowEdit = levelAllowEditList.findIndex(
      item => item.level === level
    )
    if (indexLevelAllowEdit !== -1) {
      userType = ASSIGNEE_TYPE_CONFIG[indexLevelAllowEdit]
    }
    if (userType) {
      selectPeopleStore.setAssigneePopupType(userType)
      setIsVisibleSelectUserPopup(true)
    } else {
      return message.warning('Không rõ chọn người loại nào!')
    }
  }

  const renderTitleStep = level => {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div>
          <span style={{ color: redPrimary }}>* </span>
          <span>{level?.user?.department || level.name}</span>
        </div>
      </div>
    )
  }

  const renderDescStep = level => {
    const assignTypeIndex = approvalLevelList
      .filter(el => el.allow_edit)
      .findIndex(item => item.level === level.level)
    const assigneeType =
      assignTypeIndex < ASSIGNEE_TYPE_CONFIG.length
        ? ASSIGNEE_TYPE_CONFIG[assignTypeIndex]
        : `DYNAMIC_LEVEL_${assignTypeIndex}`
    const handleDeleteUser = () => {
      if (level.allow_edit && level.username) {
        selectPeopleStore.handleRemoveSelect({
          assignee_type: ASSIGNEE_TYPE.USER,
          id: level.username,
          permission: assigneeType,
        })

        const updatedLevels = approvalLevels.map(item =>
          item.level === level.level
            ? { ...item, username: '', user: null }
            : item
        )
        setApprovalLevels(updatedLevels)

        proposalStore.setDefaultApprovalConfigUpdate(updatedLevels)
      }
    }
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: level.allow_edit ? 6 : 14,
        }}>
        {level?.user?.username ? (
          <Tag
            className={'tag-selected'}
            color={'blue'}
            closable={false}
            onClose={handleDeleteUser}>
            <AvatarHasName
              imgId={level.user?.image_id}
              size={22}
              name={level.user?.full_name}
            />
          </Tag>
        ) : (
          <Tag style={{ marginTop: 6, borderRadius: 10 }}>Chưa gán người!</Tag>
        )}
        {renderStatus(level.status, statusStep === 'error')}
        {level.comment && level.comment !== '' && (
          <div style={{ marginTop: 4, fontStyle: 'italic' }}>
            <Typography.Paragraph
              ellipsis={{
                rows: 15,
                expandable: true,
                onEllipsis: ellipsis => {
                  console.log('Ellipsis changed:', ellipsis)
                },
              }}>
              <span style={{ fontWeight: 500, marginRight: 6 }}>Lý do:</span>
              {level.comment}
            </Typography.Paragraph>
          </div>
        )}
        {renderTime(level)}
      </div>
    )
  }
  return styleCre != null ? (
    // Ở trang create chỉ để 4 cấp duyệt ở 1 hàng
    <StepsWrapper count={approvalLevels.length} i={4}>
      <Steps
        status={statusStep}
        className={'steps'}
        labelPlacement={'vertical'}
        current={currentStep}
        size={'small'}>
        {approvalLevels.map(level => {
          return (
            <Step
              key={level.level}
              title={renderTitleStep(level)}
              description={renderDescStep(level)}
            />
          )
        })}
      </Steps>
      <SelectPeoplePopup
        onlyOneUser
        handleCancelSelectUser={handleSubmitSelectUser}
        handleSubmitSelectUser={handleSubmitSelectUser}
        isVisibleSelectPeoplePopup={isVisibleSelectUserPopup}
        isUserDisableList={false}
      />
    </StepsWrapper>
  ) : (
    // Ở trang create để 5 cấp duyệt ở 1 hàng
    <StepsWrapper count={approvalLevels.length} i={5}>
      <Steps
        status={statusStep}
        className={'steps'}
        labelPlacement={'vertical'}
        current={currentStep}
        size={'small'}>
        {approvalLevels.map(level => {
          return (
            <Step
              key={level.level}
              title={renderTitleStep(level)}
              description={renderDescStep(level)}
            />
          )
        })}
      </Steps>
      <SelectPeoplePopup
        onlyOneUser
        handleCancelSelectUser={handleSubmitSelectUser}
        handleSubmitSelectUser={handleSubmitSelectUser}
        isVisibleSelectPeoplePopup={isVisibleSelectUserPopup}
        isUserDisableList={false}
      />
    </StepsWrapper>
  )
}

ProposalStepApproveDefault.propTypes = {}

export default memo(
  inject(
    'authenticationStore',
    'proposalStore',
    'selectPeopleStore'
  )(observer(ProposalStepApproveDefault))
)
